// СЕЗОННЫЙ РАЗДЕЛИТЕЛЬ МЕЖДУ СЕКЦИЯМИ.
//
// ЧТО ЭТО. Волосяная линия во всю ширину и маленькая веточка на ней:
// весной — с цветами сакуры, осенью — с кленовыми листьями. Задача у него
// не украшать, а разводить два фоновых кадра, чтобы фотографии соседних
// секций не стыковались встык.
//
// ПОЧЕМУ ПЕРЕПИСАН. Раньше всё рисовалось одним SVG с viewBox 1200×120.
// Браузер вписывает такой рисунок в блок целиком, а значит масштабирует по
// МЕНЬШЕЙ стороне: чем ниже мы делали полосу, тем сильнее сжимался рисунок —
// и «тонкий разделитель» превращался в маленькую загогулину посреди пустого
// тёмного поля, съедая при этом высоту целой секции. Отсюда и ощущение
// «занимает много места и слишком тёмный».
//
// Теперь это два независимых слоя:
//   · ЛИНИЯ — обычный div высотой 1 px во всю ширину, тоньше некуда;
//   · ВЕТОЧКА — маленький SVG в натуральную величину, лежит на линии.
// Ни один из них не тянет за собой другой: линия всегда во всю ширину,
// цветы всегда нормального размера, а высота блока — высота веточки.
//
// ДВА СЕЗОНА, и порядок на странице не случайный — он повторяет японский год:
//   season="sakura" — весна, розовые цветы (kj.gold);
//   season="momiji" — осень, кленовые листья (kj.sky, янтарь).
//
// ТРИ РАСКЛАДКИ — где на линии сидит веточка:
//   left — ближе к левому краю, right — к правому, center — по центру.
//
// Всё декоративное: aria-hidden, скринридер это не читает.

// Лепесток сакуры: узкий, с характерной выемкой на конце.
const PETAL = "M0 0 C-4.2 -2.8 -5 -9 -1.9 -12 L0 -10.2 L1.9 -12 C5 -9 4.2 -2.8 0 0 Z";

// Кленовый лист момидзи: семь острых лопастей и короткий черешок.
const MAPLE =
  "M0 0 L-1.6 -3.5 L-7.5 -2.2 L-5.4 -5.6 L-11.5 -8.4 L-6.2 -9.8 " +
  "L-8 -15.5 L-3 -12.6 L0 -19.5 L3 -12.6 L8 -15.5 L6.2 -9.8 " +
  "L11.5 -8.4 L5.4 -5.6 L7.5 -2.2 L1.6 -3.5 Z";

// Цветок сакуры: пять лепестков и тонкая сердцевина.
const Blossom = ({ x, y, size = 1, tilt = 0, opacity = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${size}) rotate(${tilt})`} opacity={opacity}>
    {[0, 72, 144, 216, 288].map((angle) => (
      <path key={angle} d={PETAL} transform={`rotate(${angle})`} className="fill-kj-gold" />
    ))}
    <circle r="1.5" className="fill-kj-dark" />
    {[20, 92, 164, 236, 308].map((angle) => (
      <line
        key={angle}
        x1="0"
        y1="0"
        x2="0"
        y2="-4.2"
        transform={`rotate(${angle})`}
        className="stroke-kj-gold"
        strokeWidth="0.6"
        strokeLinecap="round"
        opacity="0.75"
      />
    ))}
  </g>
);

// Нераскрывшийся бутон — капля на ножке.
const Bud = ({ x, y, size = 1, tilt = 0, opacity = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${size}) rotate(${tilt})`} opacity={opacity}>
    <path d="M0 0 C-2.6 -2 -3 -6 0 -8 C3 -6 2.6 -2 0 0 Z" className="fill-kj-gold" />
  </g>
);

// Кленовый лист. Прожилки нарисованы отдельными линиями поверх силуэта.
const Maple = ({ x, y, size = 1, tilt = 0, opacity = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${size}) rotate(${tilt})`} opacity={opacity}>
    <path d={MAPLE} className="fill-kj-sky" />
    <g className="stroke-kj-dark" strokeWidth="0.5" strokeLinecap="round" opacity="0.45">
      <line x1="0" y1="-1.5" x2="0" y2="-16" />
      <line x1="0" y1="-6.5" x2="-7.5" y2="-8" />
      <line x1="0" y1="-6.5" x2="7.5" y2="-8" />
      <line x1="0" y1="-9.5" x2="-5" y2="-13.5" />
      <line x1="0" y1="-9.5" x2="5" y2="-13.5" />
    </g>
    {/* черешок */}
    <line x1="0" y1="0" x2="0" y2="3.2" className="stroke-kj-sky" strokeWidth="0.8" strokeLinecap="round" />
  </g>
);

// ВЕТОЧКА. Своя система координат 150×46 — маленькая и в натуральную
// величину: рисунок не растягивается на всю ширину страницы, поэтому цветы
// остаются такими же, как на логотипе, независимо от ширины экрана.
const SPRIG = {
  branch: "M4 40 C 40 36, 62 30, 96 26 S 132 20, 146 16",
  twig: "M78 28 C 84 21, 88 15, 87 8",
  // Цветы: один крупный на конце веточки, два помельче на самой ветке.
  blossoms: [
    { x: 87, y: 8, size: 0.95, tilt: -10 },
    { x: 52, y: 32, size: 0.72, tilt: 22, opacity: 0.9 },
    { x: 122, y: 21, size: 0.6, tilt: -24, opacity: 0.8 },
  ],
  buds: [{ x: 108, y: 24, size: 0.8, tilt: -30 }],
};

// Где веточка сидит на линии: доли левого и правого отрезка.
const PLACEMENT = {
  left: ["basis-[12%]", "flex-1"],
  right: ["flex-1", "basis-[12%]"],
  center: ["flex-1", "flex-1"],
};

export default function SakuraDivider({
  variant = "left",
  season = "sakura",
  className = "",
}) {
  const momiji = season === "momiji";
  const Leaf = momiji ? Maple : Blossom;
  // Осенью бутонов не бывает — вместо них мелкие облетевшие листья.
  const Small = momiji ? Maple : Bud;
  const [leftRule, rightRule] = PLACEMENT[variant] || PLACEMENT.left;

  // Линия: к внешним краям растворяется в фоне, у веточки — плотнее.
  const rule = "h-px bg-gradient-to-r";

  return (
    <div
      aria-hidden="true"
      className={`w-full max-w-[1526px] mx-auto px-4 select-none pointer-events-none ${className}`}
    >
      <div className="flex items-center">
        <span className={`${rule} ${leftRule} from-transparent to-kj-line/70`} />

        <svg
          viewBox="0 0 150 46"
          width="150"
          height="46"
          className="flex-none overflow-visible"
          fill="none"
        >
          {/* Ветка и веточка — волосяные линии тем же цветом, что рамки */}
          <path
            d={SPRIG.branch}
            className="stroke-kj-line"
            strokeWidth="1"
            strokeLinecap="round"
          />
          <path
            d={SPRIG.twig}
            className="stroke-kj-line"
            strokeWidth="0.8"
            strokeLinecap="round"
          />

          {SPRIG.buds.map((b, i) => (
            <Small key={i} {...b} size={(b.size ?? 1) * (momiji ? 0.55 : 1)} />
          ))}
          {SPRIG.blossoms.map((b, i) => (
            <g key={i} className="sakura-sway" style={{ animationDelay: `${i * 1.3}s` }}>
              <Leaf {...b} />
            </g>
          ))}
        </svg>

        <span className={`${rule} ${rightRule} from-kj-line/70 to-transparent`} />
      </div>
    </div>
  );
}
